import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { CHANGELOG } from '../../data/changelog';
import { tokens } from '../../theme/tokens';
import { ChangeList } from './WhatsNewDialog';

const EASE = [0.16, 1, 0.3, 1] as const;

interface ReleaseHistoryDialogProps {
  open: boolean;
  onClose: () => void;
}

// Full version timeline (newest-first) rendered on demand from the footer version
// chip and the Header user menu. Reuses ChangeList/TypeTag from WhatsNewDialog so
// the tag styling stays in one place.
const ReleaseHistoryDialog: React.FC<ReleaseHistoryDialogProps> = ({ open, onClose }) => {
  const reduceMotion = useReducedMotion();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, color: tokens.ink, fontSize: '1.25rem', pb: 1.5 }}>
        Release History
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '62vh' }}>
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          <Stack spacing={0}>
            {CHANGELOG.map((entry, idx) => (
              <Box
                key={entry.version}
                sx={{
                  position: 'relative',
                  pl: 3.5,
                  pb: idx === CHANGELOG.length - 1 ? 0 : 3.5,
                  // Timeline rail: a vertical line down the left, with a dot per version.
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 5,
                    top: 20,
                    bottom: 0,
                    width: '2px',
                    bgcolor: idx === CHANGELOG.length - 1 ? 'transparent' : '#ECE4D8',
                  },
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: idx === 0 ? tokens.orange : '#fff',
                    border: `2px solid ${idx === 0 ? tokens.orange : '#DAD0C2'}`,
                    boxShadow: idx === 0 ? `0 0 0 4px rgba(220,88,41,0.12)` : 'none',
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      px: 1, py: 0.25, borderRadius: '6px', fontWeight: 800, fontSize: '0.8rem',
                      color: '#fff', bgcolor: idx === 0 ? tokens.orange : tokens.ink,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    v{entry.version}
                  </Box>
                  {idx === 0 && (
                    <Box sx={{ px: 0.75, py: 0.25, borderRadius: '6px', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', color: tokens.orange, bgcolor: 'rgba(220,88,41,0.10)', textTransform: 'uppercase' }}>
                      Latest
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ color: tokens.muted, ml: 'auto' }}>
                    {entry.date}
                  </Typography>
                </Box>
                {entry.title && (
                  <Typography variant="body2" sx={{ color: tokens.muted, mb: 1.25, fontWeight: 500 }}>
                    {entry.title}
                  </Typography>
                )}
                <ChangeList changes={entry.changes} />
              </Box>
            ))}
          </Stack>
        </motion.div>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ bgcolor: tokens.orange, '&:hover': { bgcolor: tokens.orangeDark } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseHistoryDialog;
